import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { SaveOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import { formatHumanReadable } from '@shared/utils/string';
import { getPnlOverride, updatePnlOverride } from '@client/lib/api';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import Button from '@client/components/Button';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';

const PnlOverrides = ({ voyageId }: { voyageId: number }) => {
	const [pendingChanges, setPendingChanges] = useState({});
	const [saving, setSaving] = useState(false);

	const {
		data: pnlOverride,
		refetch: refreshPnlOverride,
	} = useQuery(
		['pnlOverrides'],
		() => getPnlOverride(voyageId),
	);

	const dataSource = useMemo(() => {
		if (pnlOverride == null) {
			return [];
		}

		const copy = {
			...pnlOverride,
			...pendingChanges
		} as any;

		delete copy.type;
		delete copy.voyageId;
		delete copy.createdAt;
		delete copy.deletedAt;
		delete copy.updatedAt;
		delete copy.id;

		const keys = Object.keys(copy);

		const result = keys.map((key) => ({
			key,
			label: formatHumanReadable(key) ?? 'Unknown field',
			type: 'text',
			value: copy[key],
			editable: true,
			inputProps: {
				allowClear: true,
			}
		}));

		return result;
	}, [pnlOverride, pendingChanges]);

	const onSaveChanges = async () => {
		setSaving(true);
		if (pnlOverride == null) {
			return;
		}

		const updatedEntry = {
			...pnlOverride,
			...pendingChanges
		};

		try {
			await updatePnlOverride({
				voyageId: voyageId,
				overrideData: updatedEntry
			});
			showSuccessNotification('Override updated successfully');
			setPendingChanges({});
			refreshPnlOverride();
		} catch (e) {
			showErrorNotification('Could not update override', e as Error);
		} finally {
			setSaving(false);
		}
	};

	const hasPendingChanges = useMemo(() => {
		return !(Object.entries(pendingChanges).length === 0);
	}, [pendingChanges]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// macOS: metaKey = ⌘ , Windows: ctrlKey = Ctrl
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
				e.preventDefault(); // prevent browser save dialog
				if (!saving && hasPendingChanges) {
					onSaveChanges();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onSaveChanges, saving, hasPendingChanges]);

	return (
		<Flex vertical gap={16}>
			<Button
				disabled={!hasPendingChanges}
				pulse={hasPendingChanges}
				type="primary"
				onClick={onSaveChanges}
				icon={(<SaveOutlined />)}
			>
				Save Changes
			</Button>
			<EditableDetails
				title="Pnl Overrides"
				editing
				hideHeader
				// @ts-ignore
				items={dataSource}
				onEditValuesChange={(v) => setPendingChanges((prev) => ({ ...prev, ...v }))}
			/>
		</Flex>
	);

};

export default PnlOverrides;
